export const autocompleteShifts = (
  schedule,
  participants,
  shifts,
  days,
  holidays
) => {
  const updated = JSON.parse(JSON.stringify(schedule));

  // --- Helpers ---

  //   const getAssignedShiftCount = (pIndex) => {
  //     return Object.values(updated[pIndex]).filter(
  //       (val) => val !== "" && val !== "-"
  //     ).length;
  //   };

  //   const getHolidayShiftCount = (pIndex) => {
  //     return Object.entries(updated[pIndex]).filter(
  //       ([dayIndex, val]) =>
  //         val !== "" && val !== "-" && holidays.includes(Number(dayIndex))
  //     ).length;
  //   };

  //   // NEW: count assignments for a specific shift type on a specific day
  //   const getShiftAssignmentsForDay = (dayIndex, shiftType) => {
  //     return participants.reduce((count, _, pIndex) => {
  //       return count + (updated[pIndex][dayIndex] === shiftType ? 1 : 0);
  //     }, 0);
  //   };

  //   const respectsMinEmptyDays = (pIndex, dayToAssign) => {
  //     const minEmpty = participants[pIndex].minEmptyDaysBetweenShifts;

  //     for (let existingDay = 0; existingDay < days; existingDay++) {
  //       const existingShift = updated[pIndex][existingDay];
  //       if (existingShift !== "" && existingShift !== "-") {
  //         const daysDifference = Math.abs(existingDay - dayToAssign);
  //         if (daysDifference <= minEmpty) {
  //           return false;
  //         }
  //       }
  //     }
  //     return true;
  //   };

  // Fisher-Yates shuffle function for true randomization
  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const createAssignmentOpportunities = (schedule) => {
    const opportunities = [];

    // Calculate current minimum holiday shifts across all participants
    const holidayShiftCounts = participants.map((_, pIndex) =>
      getHolidayShiftCountForSchedule(schedule, pIndex)
    );
    const minHolidayShifts = Math.min(...holidayShiftCounts);

    shifts.forEach((shift) => {
      for (let dayIndex = 0; dayIndex < days; dayIndex++) {
        const isHoliday = holidays.includes(dayIndex);

        participants.forEach((_, pIndex) => {
          if (
            canAssignPersonForSchedule(schedule, pIndex, dayIndex, shift.name)
          ) {
            // For holidays, check fair distribution
            if (
              isHoliday &&
              !canAssignHolidayForSchedule(schedule, pIndex, minHolidayShifts)
            ) {
              return; // Skip this holiday assignment
            }

            opportunities.push({
              pIndex,
              dayIndex,
              shiftType: shift.name,
              isHoliday,
            });
          }
        });
      }
    });

    return opportunities;
  };

  // Helper functions that work with a specific schedule
  const getAssignedShiftCountForSchedule = (schedule, pIndex) => {
    return Object.values(schedule[pIndex]).filter(
      (val) => val !== "" && val !== "-"
    ).length;
  };

  const getHolidayShiftCountForSchedule = (schedule, pIndex) => {
    return Object.entries(schedule[pIndex]).filter(
      ([dayIndex, val]) =>
        val !== "" && val !== "-" && holidays.includes(Number(dayIndex))
    ).length;
  };

  const getShiftAssignmentsForDayForSchedule = (
    schedule,
    dayIndex,
    shiftType
  ) => {
    return participants.reduce((count, _, pIndex) => {
      return count + (schedule[pIndex][dayIndex] === shiftType ? 1 : 0);
    }, 0);
  };

  const respectsMinEmptyDaysForSchedule = (schedule, pIndex, dayToAssign) => {
    const minEmpty = participants[pIndex].minEmptyDaysBetweenShifts;

    for (let existingDay = 0; existingDay < days; existingDay++) {
      const existingShift = schedule[pIndex][existingDay];
      if (existingShift !== "" && existingShift !== "-") {
        const daysDifference = Math.abs(existingDay - dayToAssign);
        if (daysDifference <= minEmpty) {
          return false;
        }
      }
    }
    return true;
  };

  const canAssignPersonForSchedule = (
    schedule,
    pIndex,
    dayIndex,
    shiftType
  ) => {
    if (schedule[pIndex][dayIndex] !== "") return false;

    if (
      getAssignedShiftCountForSchedule(schedule, pIndex) >=
      participants[pIndex].maxShiftsPerPerson
    ) {
      return false;
    }

    if (
      getShiftAssignmentsForDayForSchedule(schedule, dayIndex, shiftType) >= 1
    ) {
      return false;
    }

    if (!respectsMinEmptyDaysForSchedule(schedule, pIndex, dayIndex)) {
      return false;
    }

    return true;
  };

  const canAssignHolidayForSchedule = (schedule, pIndex, minHolidayShifts) => {
    const currentPersonHolidayCount = getHolidayShiftCountForSchedule(
      schedule,
      pIndex
    );

    if (currentPersonHolidayCount >= minHolidayShifts) return true;

    for (let i = 0; i < participants.length; i++) {
      if (
        i !== pIndex &&
        getHolidayShiftCountForSchedule(schedule, i) < minHolidayShifts
      ) {
        return false;
      }
    }

    return true;
  };

  const distributeShiftsRandomly = (initialSchedule) => {
    const schedule = JSON.parse(JSON.stringify(initialSchedule));
    let opportunities = createAssignmentOpportunities(schedule);

    while (opportunities.length > 0) {
      // Filter to only valid opportunities
      opportunities = opportunities.filter((opp) =>
        canAssignPersonForSchedule(
          schedule,
          opp.pIndex,
          opp.dayIndex,
          opp.shiftType
        )
      );

      if (opportunities.length === 0) break;

      // For holidays, recheck fair distribution
      opportunities = opportunities.filter((opp) => {
        if (!opp.isHoliday) return true;

        const holidayShiftCounts = participants.map((_, pIndex) =>
          getHolidayShiftCountForSchedule(schedule, pIndex)
        );
        const minHolidayShifts = Math.min(...holidayShiftCounts);

        return canAssignHolidayForSchedule(
          schedule,
          opp.pIndex,
          minHolidayShifts
        );
      });

      if (opportunities.length === 0) break;

      // Shuffle the opportunities for random selection
      opportunities = shuffleArray(opportunities);

      // Pick the first opportunity (which is now random due to shuffle)
      const selectedOpportunity = opportunities[0];
      schedule[selectedOpportunity.pIndex][selectedOpportunity.dayIndex] =
        selectedOpportunity.shiftType;

      // Remove the selected opportunity from the list
      opportunities = opportunities.filter(
        (opp) =>
          !(
            opp.pIndex === selectedOpportunity.pIndex &&
            opp.dayIndex === selectedOpportunity.dayIndex &&
            opp.shiftType === selectedOpportunity.shiftType
          )
      );
    }

    return schedule;
  };

  // Scoring function - higher is better
  const scoreSchedule = (schedule) => {
    let score = 0;

    // 1. Completeness score - count filled shifts per day
    let totalFilledShifts = 0;
    const totalPossibleShifts = days * shifts.length;

    for (let dayIndex = 0; dayIndex < days; dayIndex++) {
      for (let shiftIndex = 0; shiftIndex < shifts.length; shiftIndex++) {
        const shift = shifts[shiftIndex];
        if (
          getShiftAssignmentsForDayForSchedule(schedule, dayIndex, shift.name) >
          0
        ) {
          totalFilledShifts++;
        }
      }
    }

    score += (totalFilledShifts / totalPossibleShifts) * 1000; // Weight completeness highly

    // 2. Fairness score - minimize variance in shift distribution
    const shiftCounts = participants.map((_, pIndex) =>
      getAssignedShiftCountForSchedule(schedule, pIndex)
    );
    const avgShifts =
      shiftCounts.reduce((a, b) => a + b, 0) / shiftCounts.length;
    const variance =
      shiftCounts.reduce(
        (sum, count) => sum + Math.pow(count - avgShifts, 2),
        0
      ) / shiftCounts.length;
    score -= variance * 10; // Penalize high variance

    // 3. Holiday fairness score
    const holidayCounts = participants.map((_, pIndex) =>
      getHolidayShiftCountForSchedule(schedule, pIndex)
    );
    const avgHolidays =
      holidayCounts.reduce((a, b) => a + b, 0) / holidayCounts.length;
    const holidayVariance =
      holidayCounts.reduce(
        (sum, count) => sum + Math.pow(count - avgHolidays, 2),
        0
      ) / holidayCounts.length;
    score -= holidayVariance * 20; // Weight holiday fairness more heavily

    return score;
  };

  // --- Execute multiple runs and pick the best ---
  let bestSchedule = null;
  let bestScore = -Infinity;

  for (let run = 0; run < 5; run++) {
    const testSchedule = distributeShiftsRandomly(updated);
    const score = scoreSchedule(testSchedule);

    if (score > bestScore) {
      bestScore = score;
      bestSchedule = testSchedule;
    }
  }

  console.log(`Best score after 5 runs: ${bestScore.toFixed(2)}`);
  return bestSchedule || updated;
};
